import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type {
	UseAutocompleteProps,
	UseAutocompleteReturnValue,
} from '@mui/base/useAutocomplete';
import type { InputHTMLAttributes } from 'react';

import IconX from '~icons/bx/x';
import IconChevronDown from '~icons/ci/chevron-down';

import { theme } from '@adflow/frontend/ui-theme';

import type { InputProps, InputStatus } from '../Inputs';
import { Input } from '../Inputs';
import { selectorStyles } from '../Selectors';

export interface HubAutocompleteRenderInputProps<
	T,
	DisableClearable extends boolean | undefined = false,
	FreeSolo extends boolean | undefined = false,
>
	extends
		Pick<
			InputProps,
			| keyof InputStatus
			| 'placeholder'
			| 'inputRef'
			| 'startAdornment'
			| 'endAdornment'
		>,
		Pick<
			UseAutocompleteProps<T, false, DisableClearable, FreeSolo>,
			'disableClearable'
		>,
		Pick<
			UseAutocompleteReturnValue<T, false, DisableClearable, FreeSolo>,
			| 'setAnchorEl'
			| 'getClearProps'
			| 'getPopupIndicatorProps'
			| 'popupOpen'
			| 'focused'
			| 'inputValue'
		> {
	inputProps: InputHTMLAttributes<HTMLInputElement>;

	testid?: string;
	styles?: {
		input?: CSSInterpolation;
	};
}

export function hubAutocompleteDefaultRenderInput<
	T,
	DisableClearable extends boolean | undefined = false,
>({
	// UseAutocompleteProps
	disableClearable,

	// UseAutocompleteReturnValue
	setAnchorEl,
	getClearProps,
	getPopupIndicatorProps,
	popupOpen,
	focused,
	inputValue,

	// Other
	inputProps,
	testid,
	styles,

	// InputProps
	disabled,
	readOnly,
	endAdornment,
	...moreInputProps
}: HubAutocompleteRenderInputProps<T, DisableClearable>) {
	const clearButton = (
		<button
			{...getClearProps()}
			type="button"
			css={{
				color: theme.colors.text.tableHeader,
				marginLeft: '0.25rem',
				/**
				 * Ultra-specific margin so the gap between the clear
				 * button and the dropdown button matches the input
				 * side padding
				 */
				marginRight: '6px',
				cursor: 'pointer',
			}}
		>
			<IconX width={20} height={20} />
		</button>
	);
	const popoverButton = (
		<button
			{...getPopupIndicatorProps()}
			type="button"
			css={selectorStyles.adornmentIcon}
			data-testid={`${testid}-button`}
			disabled={disabled}
		>
			<IconChevronDown
				css={[
					disabled &&
						css({
							color: theme.colors.text.disabled,
						}),
					popupOpen && { rotate: '180deg' },
				]}
				width={20}
				height={20}
			/>
		</button>
	);

	return (
		<Input
			{...inputProps}
			{...moreInputProps}
			disabled={disabled}
			readOnly={readOnly}
			containerRef={setAnchorEl}
			data-testid={`${testid}-input`}
			autoComplete="new-password"
			endAdornment={
				<>
					{endAdornment}
					{disabled || readOnly ? null : (
						<>
							{inputValue && !disableClearable && focused ? clearButton : null}
							{popoverButton}
						</>
					)}
				</>
			}
			containerStyle={styles?.input}
		/>
	);
}
